import React, { useMemo, useState } from 'react';
import { Box, Typography, Stack, ButtonBase } from '@mui/material';
import {
    CheckRounded,
    HourglassEmptyRounded,
    RadioButtonUncheckedRounded,
    CloseRounded,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

import { DASH, TASK_TONES } from './dashboardTokens';
import { PanelBody, MetricCell, EmptyState, RowSkeleton, Eyebrow } from './Surface';
import TaskDetailDrawer from './TaskDetailDrawer';

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

const STATUS_ICON = {
    completed: CheckRounded,
    in_progress: HourglassEmptyRounded,
    pending: RadioButtonUncheckedRounded,
    cancelled: CloseRounded,
};

// Udalosť riadka je to, čo sa s úlohou naposledy stalo — nie jej stav. Nová
// úloha a dokončená úloha sú dve rôzne správy, hoci obe visia na jednom riadku.
const EVENT_LABEL_KEY = {
    completed: 'completed',
    cancelled: 'cancelled',
    created: 'created',
};

const timeLabel = (value, language, t) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const at = dayjs(value).locale(locale);
    if (at.isSame(dayjs(), 'day')) return at.format('H:mm');
    if (at.isSame(dayjs().subtract(1, 'day'), 'day')) return t('pages.dashboard.feed.yesterday');
    return at.format('D. M.');
};

/**
 * Jeden riadok = jedna úloha a jej posledná udalosť. Vľavo uzol s farbou stavu,
 * v strede znenie úlohy a kto ju rieši, vpravo čas. Klik otvorí detail —
 * znenie úlohy sa do jedného riadka nezmestí a poznámka ani zadanie už vôbec.
 */
const TaskRow = ({ task, language, t, onOpen }) => {
    const overdue = task.status !== 'completed'
        && task.status !== 'cancelled'
        && task.due_date
        && dayjs(task.due_date).isBefore(dayjs());

    const tone = overdue ? TASK_TONES.overdue : (TASK_TONES[task.status] || TASK_TONES.pending);
    const Icon = STATUS_ICON[task.status] || RadioButtonUncheckedRounded;
    const eventLabel = t(`pages.dashboard.tasks.events.${EVENT_LABEL_KEY[task.event] || 'created'}`);

    const place = [task.project_name, task.thread_name].filter(Boolean).join(' · ');

    return (
        <ButtonBase
            onClick={() => onOpen(task)}
            sx={{
                width: '100%',
                textAlign: 'left',
                justifyContent: 'stretch',
                alignItems: 'stretch',
                borderRadius: `${DASH.radiusSm}px`,
                px: 1,
                mx: -1,
                transition: 'background-color .15s ease',
                '&:hover': { bgcolor: 'action.hover' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
            }}
        >
            <Stack direction="row" alignItems="center" sx={{ width: '100%', py: 1 }}>
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: tone,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': { fontSize: 16, color: '#fff' },
                }}
            >
                <Icon />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1, pl: 2 }}>
                <Typography
                    sx={{
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.3,
                        textDecoration: task.status === 'cancelled' ? 'line-through' : 'none',
                        color: task.status === 'cancelled' ? 'text.secondary' : 'text.primary',
                    }}
                    noWrap
                >
                    {task.description}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }} noWrap>
                    <Box component="span" sx={{ color: tone, fontWeight: 650 }}>
                        {overdue ? t('pages.dashboard.tasks.events.overdue') : eventLabel}
                    </Box>
                    {task.assigned_to_name && (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                            {` · ${task.assigned_to_name}`}
                        </Box>
                    )}
                    {!task.assigned_to_name && (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                            {` · ${t('pages.dashboard.tasks.unassigned')}`}
                        </Box>
                    )}
                    {place && (
                        <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${place}`}</Box>
                    )}
                </Typography>
            </Box>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, pl: 1.5, ...DASH.numeric }}
            >
                {timeLabel(task.event_at, language, t)}
            </Typography>
            </Stack>
        </ButtonBase>
    );
};

/**
 * Úlohy na nástenke — tri čísla a posledné dianie.
 *
 * Zamestnávateľ tu nemá úlohy riadiť (na to je appka a chat), má len vidieť,
 * či sa hýbu: koľko ich visí, čo prešvihlo termín a čo za týždeň pribudlo
 * a odišlo z „hotovo“.
 */
const TasksOverview = ({ data, loading, language, t }) => {
    const counts = data?.counts;
    const recent = data?.recent || [];
    const [openTaskId, setOpenTaskId] = useState(null);

    const tiles = useMemo(() => ([
        {
            key: 'open',
            label: t('pages.dashboard.tasks.open'),
            value: counts?.open ?? 0,
            hint: t('pages.dashboard.tasks.openHint')
                .replace('{inProgress}', counts?.in_progress ?? 0),
        },
        {
            key: 'overdue',
            label: t('pages.dashboard.tasks.overdue'),
            value: counts?.overdue ?? 0,
            hint: t('pages.dashboard.tasks.overdueHint'),
        },
        {
            key: 'completed',
            label: t('pages.dashboard.tasks.completed7d'),
            value: counts?.completed_7d ?? 0,
            hint: t('pages.dashboard.tasks.created7dHint')
                .replace('{created}', counts?.created_7d ?? 0),
        },
    ]), [counts, t]);

    if (loading) {
        return (
            <PanelBody>
                <RowSkeleton rows={5} />
            </PanelBody>
        );
    }

    // Prázdny stav je tu pravdepodobný (firma úlohy ešte nepoužíva), takže
    // nesmie vyzerať ako chyba — povie, odkiaľ sa úlohy berú.
    if (!counts?.total) {
        return (
            <PanelBody>
                <EmptyState
                    title={t('pages.dashboard.tasks.emptyTitle')}
                    hint={t('pages.dashboard.tasks.emptyHint')}
                />
            </PanelBody>
        );
    }

    return (
        <PanelBody>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                sx={{ flexWrap: 'wrap' }}
                useFlexGap
            >
                {tiles.map((tile, idx) => (
                    <MetricCell
                        key={tile.key}
                        label={tile.label}
                        value={tile.value}
                        hint={tile.hint}
                        isFirst={idx === 0}
                    />
                ))}
            </Stack>

            <Box sx={{ mt: 3.5, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Eyebrow sx={{ display: 'block', mb: 1.5 }}>
                    {t('pages.dashboard.tasks.recentLabel')}
                </Eyebrow>
                {recent.length === 0 ? (
                    <EmptyState
                        title={t('pages.dashboard.tasks.noRecent')}
                        hint={t('pages.dashboard.tasks.noRecentHint')}
                        sx={{ py: 2 }}
                    />
                ) : (
                    recent.map((task) => (
                        <TaskRow
                            key={task.id}
                            task={task}
                            language={language}
                            t={t}
                            onOpen={(row) => setOpenTaskId(row.id)}
                        />
                    ))
                )}
            </Box>

            <TaskDetailDrawer
                taskId={openTaskId}
                open={Boolean(openTaskId)}
                onClose={() => setOpenTaskId(null)}
                language={language}
                t={t}
            />
        </PanelBody>
    );
};

export default TasksOverview;
